import { MouseEvent, useEffect, useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { FaTrash } from 'react-icons/fa';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { CreateUserRoleBindingModal } from '../role-binding/CreateUserRoleBindingModal';
import { Member, RoleBinding } from '../../types';
import {
  getMemberAction,
  selectGetMemberResult,
  displayEmailState,
  createUserKeyAction,
  deleteUserKeyAction,
  download,
} from './memberSlice';
import {
  selectRoleBindings,
  listRoleBindingsAction,
  deleteRoleBindingAction,
} from '../role-binding/roleBindingSlice';

type Props = {
  tenant_id: string;
  user_id: string;
  closeCallback: (error?: SerializedError) => void;
};

export function MemberView({ tenant_id, user_id, closeCallback }: Props) {
  const dispatch = useAppDispatch();
  const get_result = useAppSelector(selectGetMemberResult);
  const role_bindings = useAppSelector(selectRoleBindings);
  const [member, setMember] = useState<Member>({
    id: user_id,
    name: '',
    username: '',
    state: 'enabled',
    description: '',
    email: '',
    email_verified: false,
    owned_by_tenant: true,
    tenant_id: tenant_id,
    keys: [],
  });
  const [show_modal, setShowModal] = useState(false);

  useEffect(() => {
    dispatch(getMemberAction({ tenant_id, user_id }));
    dispatch(listRoleBindingsAction({ tenant_id, user_ids: [user_id] }));
  }, [tenant_id, user_id, dispatch]);

  useEffect(() => {
    if (get_result) {
      if (getMemberAction.fulfilled.match(get_result)) {
        setMember(get_result.payload);
      } else if (getMemberAction.rejected.match(get_result)) {
        closeCallback(get_result.error);
      }
    }
  }, [get_result, closeCallback]);

  const handleOpenModal = () => {
    setShowModal(true);
  };
  const handleCloseModal = () => {
    dispatch(getMemberAction({ tenant_id, user_id }));
    dispatch(listRoleBindingsAction({ tenant_id, user_ids: [user_id] }));
    setShowModal(false);
  };
  const handleDeleteRoleBinding = async(e: MouseEvent, role_binding: RoleBinding) => {
    e.preventDefault();
    await dispatch(deleteRoleBindingAction({
      tenant_id,
      body: { id: role_binding.id },
    }));
    dispatch(listRoleBindingsAction({ tenant_id, user_ids: [user_id] }));
  };

  const handleCreateKey = async () => {
    const result = await dispatch(createUserKeyAction({
      tenant_id,
      user_id,
      // TBD: change to modal
      body: { description: 'Created from auth-portal' },
    }));
    if (createUserKeyAction.fulfilled.match(result)) {
      const { request_id, ...cert } = result.payload;
      download(JSON.stringify(cert, null, 2), `${user_id}-${cert.private_key_id}.json`, 'text/plain');
      dispatch(getMemberAction({ tenant_id, user_id }));
    }
  };

  const handleDeleteKey = async(e: MouseEvent, key_id: string) => {
    e.preventDefault();
    await dispatch(deleteUserKeyAction({
      tenant_id,
      user_id,
      key_id,
    }));
    dispatch(getMemberAction({ tenant_id, user_id }));
  };

  return (
    <>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">UserId</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{member.id}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Name</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{member.name}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Username</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{member.username}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Email</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{member.email}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Email State</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{displayEmailState(member)}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">Description</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{member.description}</span>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-3 col-form-label">State</label>
        <div className="col-sm-6">
          <span className="form-control-plaintext">{member.state}</span>
        </div>
      </div>
      <div className="mt-3">
        <div className="d-flex justify-content-between py-4">
          <h3>{member.keys?.length || 'No'} Keys</h3>
          <button className="btn btn-outline-primary my-2" onClick={handleCreateKey}>
            Create Key
          </button>
        </div>
        <table className="table">
          <thead className="table-dark">
            <tr>
              <th scope="col">#</th>
              <th scope="col">KeyId</th>
              <th scope="col">Description</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {member.keys?.map((key, index) => {
              return (
                <tr key={index}>
                  <th scope="row">{ index+1 }</th>
                  <td>{key.key_id}</td>
                  <td>{key.description}</td>
                  <td>
                    <a href="/#" className="text-danger" role="button"
                      onClick={(e) => handleDeleteKey(e, key.key_id)}>
                      <FaTrash />
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="role-binding-list mt-3">
        <div className="d-flex justify-content-between py-4">
          <h3>{role_bindings.length || 'No'} assigned Roles</h3>
          <button className="btn btn-primary my-2" onClick={handleOpenModal}>
            Create
          </button>
        </div>
        <table className="table">
          <thead className="table-dark">
            <tr>
              <th scope="col">#</th>
              <th scope="col">RoleId</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {role_bindings.map((role_binding, index) => {
              return (
                <tr key={index}>
                  <th scope="row">{ index+1 }</th>
                  <td>{role_binding.role_id}</td>
                  <td>
                    <a href="/#" className="text-danger" role="button"
                      onClick={(e) => handleDeleteRoleBinding(e, role_binding)}>
                      <FaTrash />
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <CreateUserRoleBindingModal
        tenant_id={tenant_id}
        user_id={user_id}
        show={show_modal}
        onClose={handleCloseModal}
      />
    </>
  );
}
